import { useEffect, useMemo, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Loader2, Check, CreditCard, Smartphone, Upload, Wallet, ExternalLink, Gift } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { useI18n } from "@/context/I18nContext";
import Footer from "@/components/Footer";
import MobileBottomNav from "@/components/MobileBottomNav";
import { ApplePayButton, GooglePayButton } from "@/components/WalletButtons";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";

function PlanCard({ plan, selected, onSelect, lang }) {
  const popular = plan.code === "1y";
  return (
    <button
      type="button"
      onClick={() => onSelect(plan.code)}
      className={`relative text-left rounded-xl border-2 p-4 transition-all ${
        selected
          ? "border-olive bg-olive/5"
          : "border-app-border bg-white hover:border-olive/40"
      }`}
      data-testid={`plan-${plan.code}`}
    >
      {popular && (
        <span className="absolute -top-2.5 right-3 bg-olive text-white text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold">
          ★
        </span>
      )}
      <div className="text-sm font-bold text-espresso">
        {lang === "ar" ? plan.label_ar : plan.label_en}
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-2xl font-heading font-bold text-espresso">{plan.bhd}</span>
        <span className="text-xs text-espresso/60">BHD</span>
      </div>
      <div className="text-[11px] text-espresso/50 mt-0.5">${plan.usd} via PayPal</div>
      <div className="text-[10px] text-espresso/40 mt-2">{plan.days} days</div>
    </button>
  );
}

export default function SubscriptionPage() {
  const { user } = useAuth();
  const { sub, refresh, loading } = useSubscription();
  const { t, lang } = useI18n();
  const [planCode, setPlanCode] = useState("3m");
  const [method, setMethod] = useState("paypal");
  const [bpData, setBpData] = useState(null);
  const [bpRef, setBpRef] = useState("");
  const [bpSubmitting, setBpSubmitting] = useState(false);
  const [bpDone, setBpDone] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const [linkRef, setLinkRef] = useState("");
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [linkDone, setLinkDone] = useState(false);

  const isExpired = sub && !sub.is_active;
  const isTrial = sub?.status === "trial";

  const plans = sub?.plans || [];
  const currentPlan = useMemo(() => plans.find((p) => p.code === planCode), [planCode, plans]);
  const bpInfo = sub?.benefitpay;

  const startBenefitPay = async () => {
    setBpDone(false);
    setBpRef("");
    try {
      const { data } = await api.post("/subscription/checkout", { plan: planCode, method: "benefitpay" });
      setBpData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start BenefitPay");
    }
  };

  const startPayPalLink = async () => {
    setLinkDone(false);
    setLinkRef("");
    try {
      const { data } = await api.post("/subscription/checkout", { plan: planCode, method: "paypal_link" });
      setLinkData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start payment");
    }
  };

  useEffect(() => {
    if (method === "benefitpay") {
      startBenefitPay();
      setLinkData(null);
    } else if (method === "paypal_link") {
      startPayPalLink();
      setBpData(null);
    } else {
      setBpData(null);
      setLinkData(null);
    }
  }, [method, planCode]);

  const submitLinkReference = async () => {
    if (!linkRef.trim()) return toast.error(t("sub.enter_txn"));
    setLinkSubmitting(true);
    try {
      await api.post("/subscription/paypal-link/submit", { plan: planCode, reference: linkRef.trim() });
      setLinkDone(true);
      toast.success(t("sub.pending_admin"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLinkSubmitting(false);
    }
  };

  const submitReference = async () => {
    if (!bpRef.trim()) return toast.error("Enter the reference number");
    setBpSubmitting(true);
    try {
      await api.post("/subscription/benefitpay/submit", { plan: planCode, reference: bpRef.trim() });
      setBpDone(true);
      toast.success(t("sub.pending_admin"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBpSubmitting(false);
    }
  };

  const captureOrder = async (orderId) => {
    await api.post("/subscription/paypal/capture", {
      order_id: orderId,
      plan: planCode,
    });
    toast.success(t("sub.thank_you"));
    await refresh();
  };

  const onPayPalApprove = async (data) => {
    try {
      await captureOrder(data.orderID);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Capture failed");
    }
  };

  const onPayPalCreate = async () => {
    try {
      const { data } = await api.post("/subscription/checkout", { plan: planCode, method: "paypal" });
      return data.order_id;
    } catch (e) {
      // Surface PayPal's actual error message + debug_id from our backend's 502 detail
      const detail = e?.response?.data?.detail || e?.message || "unknown";
      // Try to extract PayPal's structured error if present
      let parsedDetail = detail;
      try {
        const m = String(detail).match(/PayPal (?:create|auth|capture) failed:\s*(.+)/);
        if (m && m[1]) {
          const inner = JSON.parse(m[1]);
          const pieces = [];
          if (inner.name) pieces.push(inner.name);
          if (inner.message) pieces.push(inner.message);
          if (inner.debug_id) pieces.push(`debug_id=${inner.debug_id}`);
          if (Array.isArray(inner.details) && inner.details.length) {
            pieces.push(inner.details.map((d) => `${d.issue || d.field || ""}: ${d.description || ""}`).join(" | "));
          }
          parsedDetail = pieces.join(" · ");
        }
      } catch { /* keep raw detail */ }
      console.error("PayPal create order failure", { backendDetail: detail, parsed: parsedDetail, response: e?.response });
      toast.error(
        `PayPal: ${String(parsedDetail).slice(0, 250)}`,
        {
          duration: 20000,
          action: {
            label: t("sub.try_benefitpay"),
            onClick: () => setMethod("benefitpay"),
          },
        },
      );
      throw e; // PayPal SDK requires the promise to reject so the button resets
    }
  };

  return (
    <div className="min-h-screen bg-cream/30 px-4 sm:px-8 py-8 pb-24 md:pb-8">
      <MobileBottomNav />
      <div className="max-w-5xl mx-auto space-y-6" data-testid="subscription-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso">
              {t("sub.title")}
            </h1>
            <p className="text-sm text-espresso/70 mt-1">{user?.email}</p>
          </div>
          <div className="text-right">
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-olive" />
            ) : isTrial ? (
              <div className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1.5 rounded-full">
                {t("sub.trial_active")} · {sub.days_left} {t("sub.days_left")}
              </div>
            ) : sub?.is_active ? (
              <div className="inline-flex items-center gap-1.5 bg-olive/15 text-olive text-xs font-bold px-3 py-1.5 rounded-full">
                <Check className="w-3 h-3" />
                {t("sub.active")} · {sub.days_left} {t("sub.days_left")}
              </div>
            ) : (
              <div className="inline-flex items-center gap-1.5 bg-danger/15 text-danger text-xs font-bold px-3 py-1.5 rounded-full">
                {t("sub.expired")}
              </div>
            )}
            {sub?.end_date && (
              <div className="text-[11px] text-espresso/50 mt-1">
                {t("sub.expires_on")}: {new Date(sub.end_date).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>

        {isExpired && (
          <div className="bg-danger/10 border border-danger/30 text-danger rounded-xl p-3 text-sm">
            {t("sub.expired_message")}
          </div>
        )}

        {/* Plans */}
        <div>
          <h2 className="text-xs font-bold uppercase tracking-widest text-olive mb-3">
            {t("sub.choose_plan")}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {plans.map((p) => (
              <PlanCard
                key={p.code}
                plan={p}
                selected={planCode === p.code}
                onSelect={setPlanCode}
                lang={lang}
              />
            ))}
          </div>
        </div>

        {/* Redeem code */}
        <RedeemCodeBox onRedeemed={refresh} />

        {/* Method tabs */}
        <div>
          <h2 className="text-xs font-bold uppercase tracking-widest text-olive mb-3">
            {t("sub.method")}
          </h2>
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setMethod("paypal")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                method === "paypal"
                  ? "border-olive bg-olive/5 text-espresso"
                  : "border-app-border bg-white text-espresso/60 hover:border-olive/40"
              }`}
              data-testid="method-paypal"
            >
              <CreditCard className="w-4 h-4" />
              {t("sub.paypal")}
            </button>
            <button
              type="button"
              onClick={() => setMethod("paypal_link")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                method === "paypal_link"
                  ? "border-olive bg-olive/5 text-espresso"
                  : "border-app-border bg-white text-espresso/60 hover:border-olive/40"
              }`}
              data-testid="method-paypal-link"
            >
              <Wallet className="w-4 h-4" />
              {t("sub.paypal_link")}
            </button>
            <button
              type="button"
              onClick={() => setMethod("benefitpay")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                method === "benefitpay"
                  ? "border-olive bg-olive/5 text-espresso"
                  : "border-app-border bg-white text-espresso/60 hover:border-olive/40"
              }`}
              data-testid="method-benefitpay"
            >
              <Smartphone className="w-4 h-4" />
              {t("sub.benefitpay")}
            </button>
          </div>

          {/* Sections stay mounted; CSS toggling avoids PayPal SDK zoid destroy errors */}
          <div className={method === "paypal" ? "bg-white rounded-xl border border-app-border p-4" : "hidden"}>
              {PAYPAL_CLIENT_ID ? (
                <PayPalScriptProvider
                  options={{
                    clientId: PAYPAL_CLIENT_ID,
                    currency: "USD",
                    intent: "capture",
                    components: "buttons,applepay,googlepay",
                  }}
                >
                  <div className="mb-3 text-sm text-espresso/70">
                    {t("sub.continue_paypal")} · <b>${currentPlan?.usd}</b> ({lang === "ar" ? currentPlan?.label_ar : currentPlan?.label_en})
                  </div>
                  <PayPalButtons
                    key={planCode}
                    forceReRender={[planCode]}
                    style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
                    createOrder={onPayPalCreate}
                    onApprove={onPayPalApprove}
                    onError={(err) => {
                      // PayPal-side errors include details we want to surface for support
                      const msg = err?.message || String(err) || "unknown";
                      console.error("PayPal SDK error", err);
                      toast.error(`PayPal: ${msg.slice(0, 140)}`, { duration: 10000 });
                    }}
                  />
                  {/* Apple Pay / Google Pay — render only on eligible devices + merchant */}
                  <div className="mt-2 space-y-2" data-testid="wallet-buttons">
                    <ApplePayButton
                      key={`ap-${planCode}`}
                      amountUsd={currentPlan?.usd || "0"}
                      createOrder={onPayPalCreate}
                      onCaptured={captureOrder}
                    />
                    <GooglePayButton
                      amountUsd={currentPlan?.usd || "0"}
                      createOrder={onPayPalCreate}
                      onCaptured={captureOrder}
                    />
                  </div>
                  <div className="mt-3 text-[11px] text-espresso/50">
                    Mode: <b>{process.env.REACT_APP_PAYPAL_MODE || "?"}</b> · Client: <span className="font-mono">{PAYPAL_CLIENT_ID.slice(0, 8)}…{PAYPAL_CLIENT_ID.slice(-4)}</span>
                  </div>
                </PayPalScriptProvider>
              ) : (
                <div className="text-xs text-espresso/60 italic">
                  PayPal not yet configured. Awaiting Client ID from admin.
                </div>
              )}
            </div>
          <div className={method === "paypal_link" ? "bg-white rounded-xl border border-app-border p-4 space-y-3" : "hidden"} data-testid="paypal-link-section">
              <div className="text-sm text-espresso/70">{t("sub.link_instructions")}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href={linkData?.link || currentPlan?.link || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-espresso hover:bg-espresso/85 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"
                  data-testid="open-payment-link"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t("sub.open_payment")} · ${currentPlan?.usd}
                </a>
                <span className="text-xs text-espresso/50">
                  PayPal · Apple Pay · Visa · Mastercard
                </span>
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-widest text-olive font-bold">
                    {t("sub.enter_txn")}
                  </label>
                  <Input
                    value={linkRef}
                    onChange={(e) => setLinkRef(e.target.value)}
                    placeholder="e.g. 8AB12345CD678901E"
                    className="mt-1"
                    data-testid="link-reference-input"
                  />
                </div>
                <Button
                  onClick={submitLinkReference}
                  disabled={linkSubmitting || !linkData}
                  className="bg-olive hover:bg-olive/90"
                  data-testid="submit-link-reference"
                >
                  {linkSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t("sub.submit_reference")}
                </Button>
              </div>
              {linkDone && (
                <div className="bg-olive/10 border border-olive/30 text-olive text-xs p-3 rounded-lg" data-testid="link-pending-note">
                  {t("sub.pending_admin")}
                </div>
              )}
            </div>
          <div className={method === "benefitpay" ? "bg-white rounded-xl border border-app-border p-4 space-y-3" : "hidden"} data-testid="benefitpay-section">
              <div className="text-sm text-espresso/70">{t("sub.benefitpay_instructions")}</div>
              <div className="text-xs font-semibold text-olive bg-olive/10 border border-olive/20 rounded-lg px-3 py-2" data-testid="benefitpay-bahrain-note">
                {t("sub.benefitpay_bahrain_note")}
              </div>
              {bpInfo?.qr_data_url ? (
                <div className="flex flex-col items-center bg-cream-2 rounded-lg p-4">
                  <img src={bpInfo.qr_data_url} alt="BenefitPay QR" className="w-44 h-44 object-contain" />
                  <div className="mt-3 text-xs text-espresso text-center">
                    <div><b>{t("sub.benefitpay_owner")}:</b> {bpInfo.owner_name}</div>
                    <div><b>{t("sub.benefitpay_amount")}:</b> {currentPlan?.bhd} BHD</div>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 rounded-lg">
                  BenefitPay QR not yet uploaded by admin. Please use PayPal for now, or contact us.
                </div>
              )}

              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-widest text-olive font-bold">
                    {t("sub.enter_reference")}
                  </label>
                  <Input
                    value={bpRef}
                    onChange={(e) => setBpRef(e.target.value)}
                    placeholder="e.g. BP-123456789"
                    className="mt-1"
                    data-testid="bp-reference-input"
                  />
                </div>
                <Button
                  onClick={submitReference}
                  disabled={bpSubmitting || !bpData}
                  className="bg-olive hover:bg-olive/90"
                  data-testid="submit-bp-reference"
                >
                  {bpSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t("sub.submit_reference")}
                </Button>
              </div>
              {bpDone && (
                <div className="bg-olive/10 border border-olive/30 text-olive text-xs p-3 rounded-lg">
                  {t("sub.pending_admin")}
                </div>
              )}
            </div>
        </div>

        {user?.role === "admin" && <AdminQrUploader onUploaded={refresh} />}
      </div>
      <Footer />
    </div>
  );
}

function RedeemCodeBox({ onRedeemed }) {
  const { t } = useI18n();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const redeem = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.post("/subscription/redeem", { code: code.trim() });
      toast.success(t("sub.redeem_success"));
      setCode("");
      await onRedeemed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Redeem failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-app-border p-4" data-testid="redeem-code-box">
      <h2 className="text-xs font-bold uppercase tracking-widest text-olive mb-2">
        {t("sub.redeem_title")}
      </h2>
      <div className="flex gap-2 items-center">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={t("sub.redeem_ph")}
          className="max-w-[220px] font-mono uppercase"
          dir="ltr"
          data-testid="redeem-code-input"
        />
        <Button
          onClick={redeem}
          disabled={busy || !code.trim()}
          className="bg-olive hover:bg-olive/90"
          data-testid="redeem-code-button"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("sub.redeem_btn")}
        </Button>
      </div>
      <p className="text-[11px] text-espresso/50 mt-2">{t("sub.redeem_note")}</p>
      <Link to="/referrals" className="inline-flex items-center gap-1.5 text-xs font-semibold text-olive hover:underline mt-3" data-testid="sub-referral-link">
        <Gift className="w-3.5 h-3.5" />{t("ref.earn_link")}
      </Link>
    </div>
  );
}

function AdminQrUploader({ onUploaded }) {
  const { t } = useI18n();
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = String(reader.result || "");
        await api.post("/admin/benefitpay/qr", { data_url: dataUrl });
        toast.success(t("sub.qr_uploaded"));
        await onUploaded();
      };
      reader.readAsDataURL(file);
    } catch (_e) {
      void _e;
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
      <div className="text-xs font-bold uppercase tracking-widest text-amber-800 mb-2">
        {t("sub.upload_qr")} (admin only)
      </div>
      <label className="inline-flex items-center gap-2 bg-amber-600 text-white px-3 py-2 rounded-lg cursor-pointer text-sm font-semibold hover:bg-amber-700">
        <Upload className="w-4 h-4" />
        {uploading ? "Uploading…" : "Choose image"}
        <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="qr-upload-input" />
      </label>
    </div>
  );
}
